<template>
  <div class="team-page">
    <div class="background-header"></div>
    <div class="shell">
      <app-topbar @lang-change="lang = $event" />

      <div class="referral-card">
        <!-- <img class="card-border" src="@/assets/home/wallet-card-border.svg" alt="" /> -->
        <div class="card-content">
          <div class="card-label">{{ t.referralLink }}</div>
          <div class="ref-link-row">
            <span class="ref-link-text">{{ referralLink }}</span>
            <img class="copy-icon" src="@/assets/team/copy-icon.svg" @click="copyRefLink" alt="copy" />
          </div>
          <button class="primary-btn" type="button" @click="copyRefLink">
            <img class="primary-btn-bg" src="@/assets/home/btn-connect-wallet.png" alt="" />
            <span class="primary-btn-text">{{ t.copyLink }}</span>
          </button>
        </div>
      </div>

      <div class="stats-row">
        <div class="stat-card" @click="$router.push('/user-list')">
          <div class="stat-title">{{ t.directCount }}</div>
          <div class="stat-value">{{ directCount }}</div>
          <div class="stat-desc">{{ t.gen1 }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-title">{{ t.teamCount }}</div>
          <div class="stat-value">{{ teamCount }}</div>
          <div class="stat-desc">{{ t.gen1to15 }}</div>
        </div>
      </div>

      <div class="genesisnode-banner" v-if="isGenesisNode" >
          <div class="genesisnode-tag">{{ t.genesisnode }}</div>
          <div class="genesisnode-earnings" @click="openGenesisModal">
            {{ t.genesisnodeEarnings }}
            <svg class="genesisnode-earnings-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M5 2L9 6L5 10" stroke="#FF235A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
      </div>

      <div class="level-label">{{ t.currentLevel }}</div>
      <section class="level-card" @click="$router.push('/genesis-node')">
        <div class="level-body">
          <div class="level-name">{{ currentLevel }}</div>
          <div class="level-bonus">{{ t.levelBonus }} {{ levelRewardRatio }}</div>
          <van-progress
            style="margin-top: 10px;"
            :percentage="progressPercent"
            stroke-width="8px"
            color="#e51d45"
            :show-pivot="false"
          />
          <div class="level-hint">{{ levelHint }}</div>
        </div>
      </section>

      <div class="stats-row">
        <div class="stat-card">
          <div class="stat-title">{{ t.majorZone }}</div>
          <div class="stat-value">{{ majorZone }}</div>
          <div class="stat-desc">{{ t.largestBranch }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-title">{{ t.minorZone }}</div>
          <div class="stat-value">{{ minorZone }}</div>
          <div class="stat-desc">{{ t.otherBranches }}</div>
        </div>
      </div>

      <div class="section-header">
        <span class="section-label">{{ t.teamStructure }}</span>
        <img class="info-icon" src="@/assets/team/info-icon.svg" alt="" />
      </div>
      <section class="structure-card">
        <div class="structure-body">
          <div class="dept-row" v-for="(dept, i) in departments" :key="i">
            <div class="dept-top">
              <span class="dept-name">{{ dept.name }}</span>
              <span class="dept-tag" :class="{ 'dept-tag--major': dept.isMajor }">{{
                dept.isMajor ? t.major : t.minor
              }}</span>
            </div>
            <van-progress
              style="margin-top: 10px;"
              :percentage="dept.percent"
              stroke-width="8px"
              color="#e51d45"
              :show-pivot="false"
            />
          </div>
        </div>
      </section>

      <div class="section-header">
        <span class="section-label">{{ t.rewardsOverview }}</span>
        <img class="info-icon" src="@/assets/team/info-icon.svg" alt="" />
      </div>
      <section class="rewards-card">
        <div class="rewards-body">
          <div class="divider"></div>
          <div class="reward-row">
            <span class="reward-label">{{ t.managementReward }}</span>
            <span class="reward-value">{{ t.todayReward }} {{ todayTotalReward }} OC</span>
          </div>
        </div>
      </section>
    </div>

    <bottom-nav active-tab="team" />

    <div class="modal-mask" v-if="showGenesisModal" @click.self="showGenesisModal = false">
      <div class="modal-box">
        <div class="modal-title">{{ t.genesisnodeEarnings }}</div>
        <div class="modal-amount">{{ genesisProfit }} OC</div>
        <div class="modal-hint">{{ t.genesisWithdrawHint }}</div>
        <div class="modal-actions">
          <button class="modal-btn modal-btn--cancel" type="button" @click="showGenesisModal = false">{{ t.cancel }}</button>
          <button class="modal-btn modal-btn--confirm" type="button" :disabled="genesisWithdrawing" @click="onGenesisWithdraw">
            <span v-if="!genesisWithdrawing">{{ t.confirmWithdraw }}</span>
            <span v-else>Processing...</span>
          </button>
        </div>
      </div>
    </div>

    <div class="toast" v-if="toastVisible">{{ toastMsg }}</div>
  </div>
</template>

<script>
import AppTopbar from '@/components/AppTopbar.vue'
import BottomNav from '@/components/BottomNav.vue'
import { wallet } from '@/store/wallet'
import { getUser, getTeam, getGenesisNodeProfit, withdrawGenesisNodeProfit } from '@/api/auth'
import { getMessages } from '@/i18n'

const GENESIS_WHITELIST = [
  '0xe3f51904994b26df978741218aa0cedbccab3e3a',
  '0x3b535afa2a66606cb9f9f359d3fb930933519908',
  '0xa2cafd8989d23b0f99f853f4a1bc2d39c0fa8aa9',
  '0x552c8778a009933724b2e78709306913689bc093',
]

export default {
  name: 'TeamInvitePage',
  components: { AppTopbar, BottomNav },
  data() {
    return {
      lang: localStorage.getItem('lang') || 'en',
      directCount: 0,
      teamCount: 0,
      currentLevel: 'C3',
      progressPercent: 76,
      levelHint: '',
      majorZone: '180,000',
      minorZone: '76,000',
      levelRewardRatio: '--',
      isGenesisNode: false,
      todayLevelReward: '--',
      rawAreas: [],
      toastVisible: false,
      toastMsg: '',
      showGenesisModal: false,
      genesisProfit: '--',
      genesisWithdrawing: false,
    }
  },
  computed: {
    t() {
      return getMessages(this.lang)
    },
    walletAddress() {
      return wallet.address
    },
    isLoggedIn() {
      return wallet.isLoggedIn
    },
    todayTotalReward() {
      const order = parseFloat(this.todayOrderReward) || 0
      const level = parseFloat(this.todayLevelReward) || 0
      const total = order + level
      return total > 0 ? total.toFixed(2) : '--'
    },
    referralLink() {
      if (!wallet.address) return '---'
      return `${window.location.origin}/?ref=${wallet.address}`
    },
    departments() {
      return this.rawAreas.map(a => ({
        name: a.label + ' ' + this.t.teamDept + ' · ' + a.formatted + ' USDT',
        percent: a.percent,
        isMajor: a.isMajor,
      }))
    }
  },
  watch: {
    walletAddress(addr) {
      if (addr && wallet.isLoggedIn) this.loadData()
    },
    isLoggedIn(val) {
      if (val) this.loadData()
    },
  },
  mounted() {
    this.lang = localStorage.getItem('lang') || 'en'
    if (this.walletAddress && wallet.isLoggedIn) this.loadData()
  },
  methods: {
    async loadData() {
      await Promise.all([this.loadUserData(), this.loadTeamData()])
    },
    async loadUserData() {
      try {
        const res = await getUser()
        const u = res.data
        this.directCount = u.direct_count || 0
        this.teamCount = u.team_count || 0
        this.currentLevel = u.level || '--'
        this.majorZone = u.big_area || '--'
        this.minorZone = u.small_area || '--'
        this.todayOrderReward = u.today_order_reward || '--'
        this.todayLevelReward = u.today_level_reward || '--'
        this.levelRewardRatio = u.level_reward_ratio != null ? u.level_reward_ratio + '%' : '--'
        this.isGenesisNode = (u.is_paid && u.in_pool) || GENESIS_WHITELIST.includes(wallet.address?.toLowerCase())
      } catch (e) {
        // not logged in or error
      }
    },
    async loadTeamData() {
      try {
        const res = await getTeam()
        const areas = res.data.areas
        const maxArea = parseFloat(areas[0]) || 1
        const labels = ['A', 'B', 'C', 'D']
        this.rawAreas = areas.map((area, i) => {
          const val = parseFloat(area)
          return { label: labels[i], val, formatted: val.toLocaleString('en-US', { maximumFractionDigits: 2 }), isMajor: i === 0, percent: Math.round((val / maxArea) * 100) }
        })
      } catch (e) {
        console.error('loadTeamData failed', e)
      }
    },
    shortAddress(addr) {
      return addr.slice(0, 4) + '...' + addr.slice(-4)
    },
    copyRefLink() {
      if (!this.walletAddress) {
        alert(this.t.connectFirst)
        return
      }
      const url = `${window.location.origin}/openclaw?ref=${this.walletAddress}`
      this.copyToClipboard(url)
      this.showToast(this.t.linkCopied)
    },
    copyToClipboard(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(() => this.fallbackCopy(text))
      } else {
        this.fallbackCopy(text)
      }
    },
    fallbackCopy(text) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
      } catch (e) {
        console.error('Copy failed', e)
      }
      document.body.removeChild(textarea)
    },
    changeLang(lang) {
      this.lang = lang
      localStorage.setItem('lang', lang)
    },
    async openGenesisModal() {
      this.genesisProfit = '--'
      this.showGenesisModal = true
      try {
        const res = await getGenesisNodeProfit()
        this.genesisProfit = parseFloat(res.data.profit_amount || 0).toFixed(4)
      } catch (e) {
        this.genesisProfit = '0'
      }
    },
    async onGenesisWithdraw() {
      const amount = parseFloat(this.genesisProfit)
      if (!amount || amount <= 0) {
        this.showToast(this.t.insufficientBalance)
        return
      }
      this.genesisWithdrawing = true
      try {
        await withdrawGenesisNodeProfit(amount.toString())
        this.showGenesisModal = false
        this.showToast(this.t.withdrawSuccess)
        this.loadUserData()
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.error
        this.showToast(msg || 'Error')
      } finally {
        this.genesisWithdrawing = false
      }
    },
    showToast(msg) {
      this.toastMsg = msg
      this.toastVisible = true
      clearTimeout(this._toastTimer)
      this._toastTimer = setTimeout(() => {
        this.toastVisible = false
      }, 2000)
    }
  }
}
</script>

<style lang="less" scoped>
.team-page {
  min-height: 100vh;
  color: #fff;
  background-image: url(../assets/home/background.png);
  background-size: 100% 100%; /* 改为拉伸填满，不裁剪 */
  background-repeat: no-repeat; /* 防止平铺 */
  padding-bottom: 90px;
  position: relative;
}

.background-header {
  width: 100%;
  height: 200px;
  background-image: url(../assets/home/background-header.png);
  background-size: 100% 100%; /* 改为拉伸填满，不裁剪 */
  background-repeat: no-repeat; /* 防止平铺 */
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
}

.shell {
  max-width: 430px;
  margin: 0 auto;
  padding: 0 15px 24px;
  box-sizing: border-box;
}

// ---- Referral Link Card ----
.referral-card {
  margin-top: 16px;
  border-radius: 15px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  background-image: url(../assets/home/wallet-card-border.png);
  background-size: 100% 100%; /* 改为拉伸填满，不裁剪 */
  background-repeat: no-repeat; /* 防止平铺 */
}

// .card-border {
//   position: absolute;
//   top: 0;
//   left: 0;
//   width: 100%;
//   height: 100%;
//   object-fit: cover;
//   pointer-events: none;
// }

.card-content {
  position: relative;
  z-index: 1;
}

.card-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.ref-link-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 14px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 11px 12px;
}

.ref-link-text {
  flex: 1;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.95);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 8px;
}

.copy-icon {
  width: 22px;
  height: 22px;
  cursor: pointer;
  object-fit: contain;
  flex-shrink: 0;
}

.primary-btn {
  margin-top: 20px;
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
}

.primary-btn-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.primary-btn-text {
  position: relative;
  z-index: 1;
  font-size: 15px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
}

// ---- Stats Row (side by side) ----
.stats-row {
  margin-top: 16px;
  display: flex;
  gap: 15px;
}

.stat-card:first-child {
  cursor: pointer;
}

.stat-card {
  flex: 1;
  border-radius: 4px;
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  position: relative;
  // background: linear-gradient(101deg, rgba(237, 35, 60, 1) 0%, rgba(237, 35, 60, 0) 100%), rgba(255, 255, 255, 0.05);
  background-image: url(../assets/home/box-border.png);
  background-size: 100% 100%; /* 改为拉伸填满，不裁剪 */
  background-repeat: no-repeat; /* 防止平铺 */

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 4px;
    padding: 1px;
    // background: linear-gradient(104deg, #fff 5%, transparent 34%),
    //   linear-gradient(105deg, rgba(243, 40, 90, 1) 5%, rgba(255, 255, 255, 0.1) 100%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
}

.stat-title {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  font-family: 'DIN Alternate', sans-serif;
}

.stat-desc {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
}

// ---- Level Card ----
.level-card {
  margin-top: 8px;
  border-radius: 4px;
  position: relative;
  // background: linear-gradient(108deg, rgba(237, 35, 60, 1) 0%, rgba(237, 35, 60, 0) 49%), rgba(255, 255, 255, 0.05);
  background-image: url(../assets/home/level-border.png);
  background-size: 100% 100%; /* 改为拉伸填满，不裁剪 */
  background-repeat: no-repeat; /* 防止平铺 */

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 4px;
    padding: 1px;
    // background: linear-gradient(112deg, #fff 2%, transparent 16%),
    //   linear-gradient(113deg, rgba(243, 40, 90, 1) 2%, rgba(255, 255, 255, 0.1) 98%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
}

.genesisnode-banner {
  margin-top: 12px;
  height: 80px;
  border-radius: 4px;
  position: relative;
  background-image: url(../assets/team/image.png);
  background-size: 100% 100%;
  background-repeat: no-repeat;
}

.genesisnode-tag {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5px 15px;
  background: linear-gradient(148.49deg, rgba(243, 40, 90, 0.2) 0%, rgba(219, 48, 70, 0.126) 65.45%);
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #FF235A;
  border: 1px solid rgba(243, 40, 90, 0.4);
}

.genesisnode-earnings {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  padding: 5px 10px;
  gap: 5px;
  background: linear-gradient(90deg, rgba(243, 40, 90, 0.23) 3.72%, rgba(141, 23, 53, 0.23) 62.4%);
  border-radius: 90px;
  font-size: 14px;
  font-weight: 500;
  color: #FF235A;
  border: 1px solid rgba(243, 40, 90, 0.4);
  cursor: pointer;
}

.level-label {
  // padding: 12px 12px 0;
  margin-top: 12px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
  position: relative;
  z-index: 1;
}

.level-body {
  padding: 0 12px 15px;
  padding-top: 15px;
  position: relative;
  z-index: 1;
  text-align: center;
}

.level-vector {
  position: absolute;
  right: 12px;
  top: 40px;
  width: 78px;
  height: 35px;
  object-fit: contain;
}

.level-name {
  font-size: 55px;
  font-weight: 500;
  font-family: 'DM Mono', monospace;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1;
  // margin-top: 15px;
}

.level-bonus {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
  margin-top: 4px;
}

.progress-bar {
  margin-top: 18px;
  position: relative;
  height: 10px;
  border-radius: 90px;
  background: rgba(255, 255, 255, 0.05);
  overflow: hidden;
}

.progress-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 90px;
  background: linear-gradient(90deg, #e41b35, #e51d45);
}

.progress-track {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 90px;
  background: rgba(255, 255, 255, 0.05);
}

.level-hint {
  margin-top: 8px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
  text-align: left;
}

// ---- Structure Card ----
.structure-card,
.rewards-card {
  margin-top: 8px;
  border-radius: 4px;
  padding: 12px;
  position: relative;
  background-size: 100% 100%; /* 改为拉伸填满，不裁剪 */
  background-repeat: no-repeat; /* 防止平铺 */

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 4px;
    padding: 1px;
    // background: linear-gradient(99deg, #fff 2%, transparent 16%),
    //   linear-gradient(99deg, rgba(243, 40, 90, 1) 2%, rgba(255, 255, 255, 0.1) 98%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
}
.structure-card {
  background-image: url(../assets/home/structure-border.png);
}
.rewards-card {
  background-image: url(../assets/home/rewards-border.png);
}
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0;
  position: relative;
  z-index: 1;
  margin-top: 12px;
}

.section-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.info-icon {
  width: 26px;
  height: 26px;
  object-fit: contain;
}

.structure-body {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dept-row {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 16px 12px;
}

.dept-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dept-name {
  font-size: 15px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
}

.dept-tag {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;

  &--major {
    color: rgba(255, 255, 255, 0.95);
  }
}

.dept-bar {
  margin-top: 10px;
  height: 8px;
  border-radius: 90px;
  background: rgba(255, 255, 255, 0.15);
  overflow: hidden;
  position: relative;
}

.dept-bar-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 90px;
  background: linear-gradient(90deg, #e41b35, #e51d45);
}

.dept-bar-track {
  position: absolute;
  inset: 0;
  border-radius: 90px;
  background: rgba(255, 255, 255, 0.15);
}

// ---- Rewards Card ----
.rewards-body {
  position: relative;
  z-index: 1;
}

.divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.05);
}

.reward-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;

  &:last-child {
    padding-bottom: 0;
  }
}

.reward-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 500;
}

.reward-value {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
}

// ---- Genesis Modal ----
.modal-mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 99;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  box-sizing: border-box;
}

.modal-box {
  width: 100%;
  max-width: 320px;
  border-radius: 14px;
  padding: 24px 20px 20px;
  background: #1e0d1c;
  border: 1px solid rgba(255, 255, 255, 0.15);
  text-align: center;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.modal-amount {
  margin-top: 20px;
  font-size: 32px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  font-family: 'DIN Alternate', sans-serif;
  line-height: 1;
}

.modal-hint {
  margin-top: 8px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
}

.modal-actions {
  margin-top: 24px;
  display: flex;
  gap: 12px;
}

.modal-btn {
  flex: 1;
  height: 40px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &--cancel {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.7);
  }

  &--confirm {
    background: linear-gradient(180deg, #ff3f66, #e80d40);
    color: #fff;

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
}

// ---- Toast ----
.toast {
  position: fixed;
  bottom: 110px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 20px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 14px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 200;
}
</style>
